<script lang="ts">
	import type { CloudImage } from "$lib/def/seo/cloud-image";
	import type { ChangeEventHandler } from "svelte/elements";
	
	import { post } from "$lib/serv/admin/auth";
	import { get, S3_IMAGE_PATH, set } from "$lib/serv/seo";
	import { toastError } from "$lib/serv/toast";
	import { getImageDataURL, getImageHash, getSVGDataURL, getThumbDataURL } from "$lib/util/image";
	import { createEventDispatcher } from "svelte";
	import XXH from "xxhashjs";

	import Button from "$lib/ui/form/Button.svelte";
	import Modal from "$lib/ui/shared/Modal.svelte";
	
	/**
	 * In the past images were stored in the 'cimg' collection.
	 */
	export let legacyMethod = false;

	let dispatch = createEventDispatcher<{ done: CloudImage | undefined}>();
	let processing = false;
	let error = '';
	let ok = false;
	let status = 0;
	let file: File | undefined;
	let isVectorImage = false;
	let type: string | undefined;
	let size: number | undefined;
	let hash: string | undefined;
	let hashSubmission: string;
	let hashSubmissionNotFound = false;
	let thumbData: string | undefined;
	let width: number | undefined;
	let height: number | undefined;
	let uploading = false;
	let name: string | undefined;
	let complete: CloudImage | undefined;

	async function processImage(e: Parameters<ChangeEventHandler<HTMLInputElement>>[0]) {
		hash = '';
		name = '';
		error = '';
		ok = false;
		complete = undefined;
		hashSubmissionNotFound = false;
		file = e.currentTarget?.files?.[0]
		if (!file) return;
		processing = true;
		isVectorImage = file.type === 'image/svg+xml';
		let dataURL = await getImageDataURL(file);
		let thumbDataUrl: string | undefined;
		({ width, height, dataUrl:thumbDataUrl } = await getThumbDataURL(dataURL, isVectorImage))
		
		if (isVectorImage) {
			const { content, url:svgDataUrl } = await getSVGDataURL(file);
			size = content.length;
			hash = 'svg-' + XXH.h64(content, 0xf).toString(36);
			thumbData = svgDataUrl;
		} else {
			size = file.size;
			hash = await getImageHash(file);
			if (thumbDataUrl) thumbData = thumbDataUrl;
		}
		
		const [id,existing] = await get<CloudImage>(legacyMethod ? 'cimg' : 'image', hash) ?? [];
		if (existing) {
			name = existing.name ?? '';
			complete = existing;
		}
		processing = false;
	}

	async function findHash() {
		hash = '';
		name = '';
		error = '';
		ok = false;
		complete = undefined;
		hashSubmissionNotFound = false;
		if (!hashSubmission) return;
		let image = await get<CloudImage>('cimg', hashSubmission);
		image = image ?? await get<CloudImage>('image', hashSubmission);
		if (!image) {
			hashSubmissionNotFound = true;
			return;
		}
		complete = image[1];
		({ name, type, hash, size, width, height, thumbData } = complete);
		ok = true;
	}

	async function update() {
		error = '';
		if (!file) return;
		const formData = new FormData();
		// console.log('file', file.name)
    formData.append('file', file, file.name);
    formData.append('path', S3_IMAGE_PATH);
		uploading = true;
		const res = await post('/api/admin/image', formData);
		if (res.ok) {
			({ ok, status, name, error } = await res.json());
			complete = { name, type: file?.type, hash, size, width, height, thumbData }
		} else {
			error = 'Image upload failed.';
		}
		if (ok) {
			await set(legacyMethod ? 'cimg' : 'image', complete, complete?.hash);
		}
		uploading = false;
	}

</script>

<Modal on:close={() => dispatch('done')}>
	<span slot="header" class="headline-s bold">Upload image</span>
	<div class="max-w-600 m-auto body-m book">
		<div>Select an image</div>
		<input type="file" name="fileToUpload" on:change={e => processImage(e)}>
		<Button type="button" {uploading} onClick={() => update()}>Upload</Button>
		{#if !file}
			<div>Or provide the hash of an exiting image</div>
			<input type="text" bind:value={hashSubmission} on:input={() => findHash()}>
			{#if hashSubmission && hashSubmissionNotFound}
				<div class="alert alert-error">Image of hash not found.</div>
			{/if}
		{/if}
		{#if processing}
			<div class="alert alert-info">Processing image...</div>
		{/if}
		{#if hash}
			<div>
				<img src={thumbData} alt="Thumbnail">
			</div>
			<div>Dimentions: {width} x {height}</div>
			<div>Size: {((size || 0)/1024).toFixed(1)} KB</div>
			{#if !hashSubmission}
				<div>Hash: {hash}</div>
			{/if}
		{/if}
		{#if error}
			<div>S3: {name}</div>
			<div class="alert alert-error">{error}</div>
		{/if}
		{#if ok}
			<div>S3: {name}</div>
			<button class="button" on:click={() => dispatch('done', complete)}>Save</button>
			{#if !hashSubmission}
				<div class="alert alert-success">{status} OK</div>
			{/if}
		{:else if uploading}
			<div>Uploading...</div>
		{:else if hash}
			<Button type="button" {uploading} onClick={() => update()}>Upload</Button>
		{/if}
	</div>
</Modal>
